import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import { IMG, BRAND, resolveSiteImage } from "../brand";
import EditableText from "../components/EditableText";
import EditableImage from "../components/EditableImage";
import EditableI18n from "../components/EditableI18n";
import { translations } from "../i18n";

const T_EN = translations.en;
const T_FR = translations.fr;

export default function About() {
  const { t } = useLang();
  const { settings } = useSettings();
  const studioUrl = resolveSiteImage(settings, "studio");
  return (
    <main data-testid="about-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <p className="qw-overline mb-6">
            <EditableText kEn="about_overline_en" kFr="about_overline_fr" label="About overline"
              fallback={{ en: T_EN.about.overline, fr: T_FR.about.overline }} />
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-foreground text-balance mb-12">
            <EditableText kEn="about_title_en" kFr="about_title_fr" label="About title"
              fallback={{ en: T_EN.about.title, fr: T_FR.about.title }} />
          </h1>
          <div className="space-y-7 max-w-2xl">
            <p className="text-base lg:text-lg text-foreground/85 leading-relaxed font-light">
              <EditableText kEn="about_body1_en" kFr="about_body1_fr" label="About — first paragraph"
                fallback={{ en: T_EN.about.body1, fr: T_FR.about.body1 }} />
            </p>
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed font-light">
              <EditableText kEn="about_body2_en" kFr="about_body2_fr" label="About — second paragraph"
                fallback={{ en: T_EN.about.body2, fr: T_FR.about.body2 }} />
            </p>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[3/4] overflow-hidden qw-grain">
            <EditableImage imageKey="studio" label="Studio photo" />
            <img
              src={studioUrl}
              alt="Queensway Studio"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      {/* Pillars */}
      <section className="px-6 md:px-12 lg:px-20 py-24 grid grid-cols-1 md:grid-cols-3 gap-12">
        {t.about.pillars.map((p, i) => (
          <div key={i} data-testid={`pillar-${i}`} className="border-t border-primary/40 pt-8">
            <p className="qw-num font-serif text-4xl text-primary mb-6">0{i + 1}</p>
            <h3 className="font-serif text-2xl text-foreground mb-3"><EditableI18n k={`about.pillars.${i}.t`} label={`Pillar ${i + 1} title`} /></h3>
            <p className="text-sm text-muted-foreground leading-relaxed"><EditableI18n k={`about.pillars.${i}.d`} label={`Pillar ${i + 1} text`} /></p>
          </div>
        ))}
      </section>

      {/* Locations */}
      <section className="px-6 md:px-12 lg:px-20 py-24 grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
        <div className="bg-background p-10 md:p-14">
          <MapPin size={20} strokeWidth={1} className="text-primary mb-5" />
          <p className="qw-overline mb-3"><EditableText kEn="txt_about_sn_label_en" kFr="txt_about_sn_label_fr" label="Senegal label" fallback={{ en: "Senegal", fr: "Sénégal" }} /></p>
          <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
            <EditableI18n k="about.locDakar" label="Dakar location title" />
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            {BRAND.locationDakar}
          </p>
          <a href={`tel:${BRAND.phoneE164}`} className="qw-link-underline text-sm text-primary tracking-wider">
            {BRAND.phone}
          </a>
        </div>
        <div className="bg-background p-10 md:p-14">
          <MapPin size={20} strokeWidth={1} className="text-primary mb-5" />
          <p className="qw-overline mb-3"><EditableText kEn="txt_about_uk_label_en" kFr="txt_about_uk_label_fr" label="UK label" fallback={{ en: "United Kingdom", fr: "Royaume-Uni" }} /></p>
          <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
            <EditableI18n k="about.locLondon" label="London location title" />
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            {BRAND.locationLondon}
          </p>
          <a href={`mailto:${BRAND.email}`} className="qw-link-underline text-sm text-primary tracking-wider">
            {BRAND.email}
          </a>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 text-center">
        <Link
          to="/booking"
          data-testid="about-cta-booking"
          className="qw-btn qw-btn-primary inline-flex"
        >
          <EditableI18n k="nav.bookNow" label="Book Now button" /> <ArrowUpRight size={16} strokeWidth={1.2} />
        </Link>
      </section>
    </main>
  );
}
